import { LazyFallback } from "@msuite/ui/loading-screen";
import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createMeta, createRowActions } from "@msuite/ui/table";
import { ProjectInvoiceTag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { formatNumber } from "@msuite/utils/numbers";
import { getProjectInvoiceTypeString } from "@msuite/utils/projects";
import { guardString } from "@msuite/utils/strings";
import type { ProjectInvoice } from "@msuite/utils/types";
import { lazy, Suspense } from "react";
import { InvoicesTableState } from "@/components/invoices";
import { NoteText } from "@/components/notes";
import { RowFiles } from "@/components/tables";
import { RowActions } from "./row-actions";

const UserCard = lazy(() => import("@/components/cards/user-card"));

const columnHelper = createColumnHelper<ProjectInvoice>();

export const columns = [
	columnHelper.accessor("number", {
		id: "number",
		header: localized("invoices.number"),
		cell: (cell) => cell.getValue(),
		meta: createMeta({ isTabular: true }),
	}),
	columnHelper.accessor("createdBy", {
		id: "createdBy",
		header: localized("general_fields.created_by"),
		cell: (cell) => (
			<Suspense fallback={<LazyFallback variant="inline" />}>
				<UserCard
					variant="plain"
					userId={cell.getValue()}
				/>
			</Suspense>
		),
	}),
	columnHelper.accessor("creationDate", {
		id: "creationDate",
		header: localized("invoices.creation_date"),
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).dateFormat() : guardString("")),
	}),
	columnHelper.accessor("calculatedTo", {
		id: "calculatedTo",
		header: localized("invoices.calculated_to"),
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).dateFormat() : guardString("")),
	}),
	columnHelper.accessor((row) => localized(getProjectInvoiceTypeString(row.type)), {
		id: "type",
		header: localized("invoices.type"),
		cell: (cell) => (
			<ProjectInvoiceTag
				type={cell.row.original.type}
				variant="plain"
			/>
		),
	}),
	columnHelper.accessor("progressInvoiceNumber", {
		id: "progressInvoiceNumber",
		header: localized("invoices.progress_invoice_number"),
		cell: (cell) => guardString(cell.getValue()),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("sum", {
		id: "sum",
		header: localized("invoices.sum_net"),
		cell: (cell) => formatNumber(cell.getValue(), "currency"),
		meta: createMeta({
			isNumeric: true,
		}),
	}),
	columnHelper.accessor("state", {
		id: "state",
		header: localized("general_fields.state"),
		cell: (cell) => <InvoicesTableState invoice={cell.row.original} />,
		meta: createMeta({
			withoutPadding: true,
		}),
	}),
	columnHelper.accessor("note", {
		id: "note",
		header: localized("invoices.note"),
		cell: (cell) => (
			<NoteText
				note={guardString(cell.getValue())}
				withPopover
			/>
		),
	}),
	columnHelper.accessor((row) => row.invoiceFiles.length, {
		id: "files",
		header: localized("files.files"),
		cell: (cell) => (
			<RowFiles
				files={cell.row.original.invoiceFiles}
				unlink={{
					tableName: "outgoingInvoiceFiles",
					foreignKey: "outgoingInvoiceId",
					identifier: cell.row.original.id,
				}}
				parent={{ subject: "Invoice", resource: cell.row.original }}
			/>
		),
	}),
	createRowActions<ProjectInvoice>((cell) => <RowActions invoice={cell.row.original} />),
];
